import React, {useEffect} from 'react';

import {Stack} from '@mui/material';
import {NetworkTypesEnum} from 'domain/usecases';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {ScrollToTop} from 'presentation/components';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle} from 'presentation/utils';
import {BookingErrorState, BookingLoadingState, BookingNotFoundState} from '../../components';
import {DisabilityEnum, FlowEnum, NearNeighborhoodEnum, PATH_TITLE} from '../../constants';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {BookingNotFoundStateVariantEnum} from '../../components/BookingNotFoundState/types';
import {useBookingUnits} from '../../hooks/useBookingUnits';
import {SelfNetwork} from './components/SelfNetwork';
import {SupportAccreditedNetwork} from './components/SupportAccreditedNetwork';
import {UnitSelectionProps} from './types';

const {booking} = translations['pt-br'].others.sidebar;
const {loadingError, loadingState} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function UnitSelection({
	flowType,
	handleNextStep,
	handlePrevStep,
}: UnitSelectionProps) {
	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});
	const bookingData = useBookingStore(state => state.bookingData);
	const {
		SEARCH_PARAMS: {disability, includeNeighborhoodDistricts, localityDistance},
	} = useQueryParams();
	const {
		unitResponse,
		handleFetchUnits,
		IS_ERROR,
		IS_LOADING,
		SHOW_NOT_FOUND_STATE,
		filteredNetworkList,
		healthRequestSchedulePermission,
		specialtyNotAvailableOnline,
		shouldShowSupportAccreditedNetwork,
		shouldShowSelfNetwork,
		shouldShowEmptyAppointmentsCard,
		showSpecialtyNotAvailableInSelfNetwork,
	} = useBookingUnits({
		flowType,
	});

	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isHealthOrExamOrOdontoFlow = isHealthFlow || isExamFlow || isOdontoFlow;
	const isUsingGeolocation = !bookingData.isManualLocation && isHealthOrExamOrOdontoFlow;

	const unitParams = {
		cityName: bookingData.cityName,
		flowType,
		specialtyCode: bookingData.specialtyCode,
		subSpecialtyCode: bookingData.subSpecialtyCode,
		stateCode: bookingData.ufCode,
		disability: (disability as DisabilityEnum) || DisabilityEnum.ALL,
		districtName: bookingData.districtName,
		includeNeighborhoodDistricts:
			(includeNeighborhoodDistricts as NearNeighborhoodEnum) ||
			NearNeighborhoodEnum.NOT_INCLUDE,
		distanceFilterValue: localityDistance,
		typeLocalName: bookingData.typeLocalName,
		citiesNames: bookingData?.citiesNames,
		coordinates: bookingData.coordinates,
		isUsingGeolocation,
		hasGeoDistrictsFilter: bookingData.hasGeoDistrictsFilter,
		allDistrictsSelected: bookingData.allDistrictsSelected,
		providerCode: bookingData.professionalCode || undefined,
		isFavoriteDoctorsFlow: bookingData?.isFavoriteDoctorsFlow,
	};

	useEffect(() => {
		handleFetchUnits(unitParams);
	}, [localityDistance, disability, includeNeighborhoodDistricts, bookingData]);

	if (IS_LOADING) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrevStep}
				variant="skeleton"
			/>
		);
	}

	if (IS_ERROR) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrevStep}
				handleRefresh={() => handleFetchUnits(unitParams)}
			/>
		);
	}

	if (SHOW_NOT_FOUND_STATE) {
		return (
			<BookingNotFoundState
				flowType={flowType}
				pageTitle={pageTitle}
				handleBack={handlePrevStep}
				variant={BookingNotFoundStateVariantEnum.UNIT}
				showHeader={false}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_UNIDADES_AGENDAMENTO_DISPONIVEL}
				healthRequestSchedulePermission={healthRequestSchedulePermission}
				specialtyNotAvailableOnline={specialtyNotAvailableOnline}
			/>
		);
	}

	return (
		<Stack gap={2}>
			{shouldShowSelfNetwork ? (
				<SelfNetwork
					list={filteredNetworkList}
					handleNextStep={handleNextStep}
					flowType={flowType}
					networkType={unitResponse.networkType.name as NetworkTypesEnum}
					shouldShowEmptyAppointmentsCard={shouldShowEmptyAppointmentsCard}
					showSpecialtyNotAvailableCard={showSpecialtyNotAvailableInSelfNetwork}
				/>
			) : null}

			{shouldShowSupportAccreditedNetwork ? (
				<SupportAccreditedNetwork
					list={filteredNetworkList}
					handleNextStep={handleNextStep}
					flowType={flowType}
				/>
			) : null}

			<ScrollToTop />
		</Stack>
	);
}
